import apiClient from './apiClient';

/**
 * Helper function to handle errors
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    const err = new Error(message);
    // Preserve the backend error envelope so callers can branch on the code
    // (e.g. EMPLOYEE_ALREADY_ASSIGNED offers "Move instead" from details).
    err.code = error.response?.data?.error?.code;
    err.details = error.response?.data?.error?.details || null;
    err.status = error.response?.status;
    return err;
  }
  return error;
};

// Flag to use mock data for development
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

// Mock data
const mockUsers = [
  {
    id: 1,
    email: 'admin@example.com',
    firstName: 'Admin',
    lastName: 'User',
    role: 'admin',
    isSuperAdmin: true,
    isFirstLogin: false,
    isActive: true,
    createdAt: '2023-01-01T00:00:00.000Z',
    lastLogin: '2023-03-15T08:30:00.000Z'
  },
  {
    id: 2,
    email: 'employee1@example.com',
    firstName: 'John',
    lastName: 'Smith',
    role: 'employee',
    isSuperAdmin: false,
    isFirstLogin: false,
    isActive: true,
    createdAt: '2023-01-05T00:00:00.000Z',
    lastLogin: '2023-03-14T10:15:00.000Z'
  },
  {
    id: 3,
    email: 'employee2@example.com',
    firstName: 'Jane',
    lastName: 'Doe',
    role: 'employee',
    isSuperAdmin: false,
    isFirstLogin: false,
    isActive: true,
    createdAt: '2023-01-10T00:00:00.000Z',
    lastLogin: '2023-03-13T14:20:00.000Z'
  },
  {
    id: 4,
    email: 'employee3@example.com',
    firstName: 'Alex',
    lastName: 'Johnson',
    role: 'employee',
    isSuperAdmin: false,
    isFirstLogin: false,
    isActive: false,
    createdAt: '2023-01-15T00:00:00.000Z',
    lastLogin: '2023-02-01T09:45:00.000Z'
  },
  {
    id: 5,
    email: 'customer1@acme.com',
    firstName: 'Robert',
    lastName: 'Williams',
    role: 'customer',
    isSuperAdmin: false,
    isFirstLogin: false,
    isActive: true,
    createdAt: '2023-01-20T00:00:00.000Z',
    lastLogin: '2023-03-10T16:30:00.000Z'
  },
  {
    id: 6,
    email: 'customer2@globex.com',
    firstName: 'Emily',
    lastName: 'Davis',
    role: 'customer',
    isSuperAdmin: false,
    isFirstLogin: false,
    isActive: true,
    createdAt: '2023-01-25T00:00:00.000Z',
    lastLogin: '2023-03-12T11:05:00.000Z'
  },
  {
    id: 7,
    email: 'customer3@initech.com',
    firstName: 'Michael',
    lastName: 'Brown',
    role: 'customer',
    isSuperAdmin: false,
    isFirstLogin: false,
    isActive: true,
    createdAt: '2023-01-30T00:00:00.000Z',
    lastLogin: '2023-03-11T13:45:00.000Z'
  }
];

// Mock customer data
const mockCustomers = [
  {
    id: 1,
    name: 'Acme Corporation',
    contactEmail: 'contact@acme.com',
    contactPhone: '+1 (555) 123-4567',
    address: '123 Main St, New York, NY 10001',
    industry: 'Technology',
    isActive: true,
    createdAt: '2023-01-05T00:00:00.000Z',
    assignedEmployees: [2, 3],
    projects: [
      {
        id: 1,
        name: 'Website Redesign',
        startDate: '2023-02-01T00:00:00.000Z',
        endDate: '2023-05-31T00:00:00.000Z',
        status: 'in_progress'
      }
    ]
  },
  {
    id: 2,
    name: 'Globex Corporation',
    contactEmail: 'info@globex.com',
    contactPhone: '+1 (555) 987-6543',
    address: '456 Park Ave, San Francisco, CA 94103',
    industry: 'Manufacturing',
    isActive: true,
    createdAt: '2023-01-10T00:00:00.000Z',
    assignedEmployees: [2],
    projects: [
      {
        id: 2,
        name: 'ERP Implementation',
        startDate: '2023-01-15T00:00:00.000Z',
        endDate: '2023-07-15T00:00:00.000Z',
        status: 'in_progress'
      }
    ]
  },
  {
    id: 3,
    name: 'Initech',
    contactEmail: 'contact@initech.com',
    contactPhone: '+1 (555) 765-4321',
    address: '789 Office Park, Austin, TX 78701',
    industry: 'Finance',
    isActive: true,
    createdAt: '2023-01-15T00:00:00.000Z',
    assignedEmployees: [3, 4],
    projects: [
      {
        id: 3,
        name: 'Mobile Banking App',
        startDate: '2023-03-01T00:00:00.000Z',
        endDate: '2023-08-31T00:00:00.000Z',
        status: 'in_progress'
      }
    ]
  },
  {
    id: 4,
    name: 'Cyberdyne Systems',
    contactEmail: 'info@cyberdyne.com',
    contactPhone: '+1 (555) 234-5678',
    address: '101 Tech Drive, Boston, MA 02110',
    industry: 'Technology',
    isActive: true,
    createdAt: '2023-01-20T00:00:00.000Z',
    assignedEmployees: [2, 3, 4],
    projects: [
      {
        id: 4,
        name: 'AI Research Platform',
        startDate: '2023-02-15T00:00:00.000Z',
        endDate: '2023-11-30T00:00:00.000Z',
        status: 'in_progress'
      }
    ]
  },
  {
    id: 5,
    name: 'Stark Industries',
    contactEmail: 'projects@stark.com',
    contactPhone: '+1 (555) 876-5432',
    address: '200 Innovation Way, Malibu, CA 90265',
    industry: 'Engineering',
    isActive: false,
    createdAt: '2023-01-25T00:00:00.000Z',
    assignedEmployees: [],
    projects: [
      {
        id: 5,
        name: 'Clean Energy Initiative',
        startDate: '2023-01-05T00:00:00.000Z',
        endDate: '2023-04-10T00:00:00.000Z',
        status: 'completed'
      }
    ]
  }
];

const mockStatistics = {
  totalUsers: 7,
  activeUsers: 6,
  usersByRole: {
    admin: 1,
    employee: 3,
    customer: 3
  },
  newUsersThisMonth: 0,
  activeUsersThisMonth: 6
};

/**
 * Get users (admin)
 * @param {object} params - Filter parameters
 * @returns {Promise} - Promise containing users
 */
export const getUsers = async (params = {}) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Filter users based on params
      let filteredUsers = [...mockUsers];
      
      if (params.role) {
        filteredUsers = filteredUsers.filter(user => user.role === params.role);
      }
      
      if (params.isActive !== undefined) {
        filteredUsers = filteredUsers.filter(user => user.isActive === params.isActive);
      }
      
      if (params.search) {
        const search = params.search.toLowerCase();
        filteredUsers = filteredUsers.filter(user =>
          user.email.toLowerCase().includes(search) ||
          user.firstName.toLowerCase().includes(search) ||
          user.lastName.toLowerCase().includes(search)
        );
      }
      
      // Pagination
      const page = params.page || 1;
      const limit = params.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = page * limit;
      
      const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
      
      setTimeout(() => resolve({
        users: paginatedUsers,
        total: filteredUsers.length,
        page,
        totalPages: Math.ceil(filteredUsers.length / limit)
      }), 500);
    });
  }
  
  try {
    const response = await apiClient.get('/admin/users', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get user by ID (admin)
 * @param {string} id - User ID
 * @returns {Promise} - Promise containing user
 */
export const getUserById = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const user = mockUsers.find(user => user.id == id);
      
      if (user) {
        setTimeout(() => resolve(user), 500);
      } else {
        setTimeout(() => reject(new Error('User not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.get(`/admin/users/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Create user (admin)
 * @param {object} userData - User data
 * @returns {Promise} - Promise containing created user
 */
export const createUser = async (userData) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      const newUser = {
        id: mockUsers.length + 1,
        ...userData,
        isSuperAdmin: userData.role === 'admin' && userData.isSuperAdmin === true,
        isFirstLogin: true,
        createdAt: new Date().toISOString(),
        lastLogin: null
      };
      
      mockUsers.push(newUser);
      setTimeout(() => resolve(newUser), 500);
    });
  }
  
  try {
    const response = await apiClient.post('/admin/users', userData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update user (admin)
 * @param {string} id - User ID
 * @param {object} userData - Updated user data
 * @returns {Promise} - Promise containing updated user
 */
export const updateUser = async (id, userData) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockUsers.findIndex(user => user.id == id);
      
      if (index !== -1) {
        mockUsers[index] = { ...mockUsers[index], ...userData };
        setTimeout(() => resolve(mockUsers[index]), 500);
      } else {
        setTimeout(() => reject(new Error('User not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.put(`/admin/users/${id}`, userData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Delete user (admin)
 * @param {string} id - User ID
 * @returns {Promise} - Promise containing success message
 */
export const deleteUser = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockUsers.findIndex(user => user.id == id);
      
      if (index !== -1) {
        mockUsers.splice(index, 1);
        setTimeout(() => resolve({ message: 'User deleted successfully' }), 500);
      } else {
        setTimeout(() => reject(new Error('User not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.delete(`/admin/users/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Activate user (admin)
 * @param {string} id - User ID
 * @returns {Promise} - Promise containing updated user
 */
export const activateUser = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockUsers.findIndex(user => user.id == id);
      
      if (index !== -1) {
        mockUsers[index].isActive = true;
        setTimeout(() => resolve(mockUsers[index]), 500);
      } else {
        setTimeout(() => reject(new Error('User not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.put(`/admin/users/${id}/activate`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Deactivate user (admin)
 * @param {string} id - User ID
 * @returns {Promise} - Promise containing updated user
 */
export const deactivateUser = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockUsers.findIndex(user => user.id == id);
      
      if (index !== -1) {
        mockUsers[index].isActive = false;
        setTimeout(() => resolve(mockUsers[index]), 500);
      } else {
        setTimeout(() => reject(new Error('User not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.put(`/admin/users/${id}/deactivate`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Reset user password (admin)
 * @param {string} id - User ID
 * @returns {Promise} - Promise containing success message
 */
export const resetUserPassword = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockUsers.findIndex(user => user.id == id);
      
      if (index !== -1) {
        setTimeout(() => resolve({ 
          message: 'Password reset email sent to user',
          temporaryPassword: 'Temp123!' // Only for mock data
        }), 500);
      } else {
        setTimeout(() => reject(new Error('User not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.post(`/admin/users/${id}/reset-password`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get active employees who have NOT saved their hours for a given week (admin)
 * @param {string} [weekStart] - Any date within the target week ('YYYY-MM-DD').
 *   Omit to let the backend default to the current/previous week. The backend
 *   normalizes the value to the configured week-start date.
 * @returns {Promise} - Promise resolving to { weekStart, missing: [{ employeeId, name, email }], count }
 */
export const getMissingWeekHours = async (weekStart) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => resolve({
        weekStart: weekStart || null,
        missing: [],
        count: 0
      }), 500);
    });
  }

  try {
    const params = weekStart ? { weekStart } : {};
    const response = await apiClient.get('/admin/reports/week-missing', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Missing "Finished" report (REQ #9): days not marked finished, grouped by
 * customer → employee → days.
 * @param {string} [start] - window start 'YYYY-MM-DD' (default: month start)
 * @param {string} [end] - window end 'YYYY-MM-DD' (default: today)
 */
export const getMissingFinishedReport = async (start, end) => {
  try {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await apiClient.get('/admin/reports/missing-finished', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Send an URGENT, localized "please finish your hours" reminder to one employee
 * (CCs the admins). REQ #9.
 */
export const remindEmployeeFinished = async (employeeId, start, end) => {
  try {
    const response = await apiClient.post('/admin/reports/remind-finished', { employeeId, start, end });
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get admin dashboard statistics
 * @returns {Promise} - Promise containing dashboard statistics
 */
export const getDashboardStatistics = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockStatistics), 500);
    });
  }
  
  try {
    const response = await apiClient.get('/admin/dashboard/statistics');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get customers (admin)
 * @param {object} params - Filter parameters
 * @returns {Promise} - Promise containing customers
 */
// Item 12: per-customer invoicing readiness, proxied from VikarCRM. One call
// for the whole page rather than one per customer row.
export const getInvoicingCustomerStatus = async () => {
  const response = await apiClient.get('/admin/invoicing/customer-status');
  return response.data.data;
};

export const getCustomers = async (params = {}) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Filter customers based on params
      let filteredCustomers = [...mockCustomers];
      
      if (params.industry) {
        filteredCustomers = filteredCustomers.filter(customer => customer.industry === params.industry);
      }
      
      if (params.isActive !== undefined) {
        filteredCustomers = filteredCustomers.filter(customer => customer.isActive === params.isActive);
      }
      
      if (params.search) {
        const search = params.search.toLowerCase();
        filteredCustomers = filteredCustomers.filter(customer =>
          customer.name.toLowerCase().includes(search) ||
          customer.contactEmail.toLowerCase().includes(search)
        );
      }
      
      // Pagination
      const page = params.page || 1;
      const limit = params.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = page * limit;
      
      const paginatedCustomers = filteredCustomers.slice(startIndex, endIndex);
      
      setTimeout(() => resolve({
        customers: paginatedCustomers,
        total: filteredCustomers.length,
        page,
        totalPages: Math.ceil(filteredCustomers.length / limit)
      }), 500);
    });
  }
  
  try {
    const response = await apiClient.get('/admin/customers', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get customer by ID (admin)
 * @param {string} id - Customer ID
 * @returns {Promise} - Promise containing customer
 */
export const getCustomerById = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const customer = mockCustomers.find(customer => customer.id == id);
      
      if (customer) {
        setTimeout(() => resolve(customer), 500);
      } else {
        setTimeout(() => reject(new Error('Customer not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.get(`/admin/customers/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Create customer (admin)
 * @param {object} customerData - Customer data
 * @returns {Promise} - Promise containing created customer
 */
export const createCustomer = async (customerData) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      const newCustomer = {
        id: mockCustomers.length + 1,
        ...customerData,
        createdAt: new Date().toISOString(),
        projects: [],
        assignedEmployees: customerData.assignedEmployees || []
      };
      
      mockCustomers.push(newCustomer);
      setTimeout(() => resolve(newCustomer), 500);
    });
  }
  
  try {
    const response = await apiClient.post('/admin/customers', customerData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update customer (admin)
 * @param {string} id - Customer ID
 * @param {object} customerData - Updated customer data
 * @returns {Promise} - Promise containing updated customer
 */
export const updateCustomer = async (id, customerData) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockCustomers.findIndex(customer => customer.id == id);
      
      if (index !== -1) {
        mockCustomers[index] = { 
          ...mockCustomers[index], 
          ...customerData,
          projects: mockCustomers[index].projects // Preserve projects 
        };
        setTimeout(() => resolve(mockCustomers[index]), 500);
      } else {
        setTimeout(() => reject(new Error('Customer not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.put(`/admin/customers/${id}`, customerData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Delete customer (admin)
 * @param {string} id - Customer ID
 * @returns {Promise} - Promise containing success message
 */
export const deleteCustomer = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockCustomers.findIndex(customer => customer.id == id);
      
      if (index !== -1) {
        mockCustomers.splice(index, 1);
        setTimeout(() => resolve({ message: 'Customer deleted successfully' }), 500);
      } else {
        setTimeout(() => reject(new Error('Customer not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.delete(`/admin/customers/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Activate customer (admin)
 * @param {string} id - Customer ID
 * @returns {Promise} - Promise containing updated customer
 */
export const activateCustomer = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockCustomers.findIndex(customer => customer.id == id);
      
      if (index !== -1) {
        mockCustomers[index].isActive = true;
        setTimeout(() => resolve(mockCustomers[index]), 500);
      } else {
        setTimeout(() => reject(new Error('Customer not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.put(`/admin/customers/${id}/activate`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Deactivate customer (admin)
 * @param {string} id - Customer ID
 * @returns {Promise} - Promise containing updated customer
 */
export const deactivateCustomer = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockCustomers.findIndex(customer => customer.id == id);
      
      if (index !== -1) {
        mockCustomers[index].isActive = false;
        setTimeout(() => resolve(mockCustomers[index]), 500);
      } else {
        setTimeout(() => reject(new Error('Customer not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.put(`/admin/customers/${id}/deactivate`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Assign employees to customer (admin)
 * @param {string} id - Customer ID
 * @param {array} employeeIds - Array of employee IDs
 * @returns {Promise} - Promise containing updated customer
 */
export const assignEmployeesToCustomer = async (id, employeeIds) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockCustomers.findIndex(customer => customer.id == id);
      
      if (index !== -1) {
        mockCustomers[index].assignedEmployees = employeeIds;
        setTimeout(() => resolve(mockCustomers[index]), 500);
      } else {
        setTimeout(() => reject(new Error('Customer not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.post(`/admin/customers/${id}/employees`, { employeeIds });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get available industry options (admin)
 * @returns {Promise} - Promise containing list of industries
 */
export const getIndustryOptions = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Extract unique industries from mock data
      const industries = [...new Set(mockCustomers.map(customer => customer.industry))];
      setTimeout(() => resolve(industries), 300);
    });
  }
  
  try {
    const response = await apiClient.get('/admin/reference/industries');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get employees (admin)
 * @param {object} params - Filter parameters
 * @returns {Promise} - Promise containing employees with their assignments
 */
export const getEmployees = async (params = {}) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      // Filter employees from users
      let filteredEmployees = mockUsers.filter(user => user.role === 'employee');
      
      if (params.isActive !== undefined) {
        filteredEmployees = filteredEmployees.filter(employee => employee.isActive === params.isActive);
      }
      
      if (params.search) {
        const search = params.search.toLowerCase();
        filteredEmployees = filteredEmployees.filter(employee =>
          employee.email.toLowerCase().includes(search) ||
          employee.firstName.toLowerCase().includes(search) ||
          employee.lastName.toLowerCase().includes(search)
        );
      }
      
      if (params.customerId) {
        // Filter employees assigned to specific customer
        const customer = mockCustomers.find(c => c.id == params.customerId);
        if (customer) {
          filteredEmployees = filteredEmployees.filter(employee => 
            customer.assignedEmployees.includes(employee.id)
          );
        }
      }
      
      // Enrich employee data with customer assignments
      const enrichedEmployees = filteredEmployees.map(employee => {
        const assignedCustomers = mockCustomers
          .filter(customer => customer.assignedEmployees.includes(employee.id))
          .map(customer => ({
            id: customer.id,
            name: customer.name
          }));
        
        return {
          ...employee,
          assignedCustomers
        };
      });
      
      // Pagination
      const page = params.page || 1;
      const limit = params.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = page * limit;
      
      const paginatedEmployees = enrichedEmployees.slice(startIndex, endIndex);
      
      setTimeout(() => resolve({
        employees: paginatedEmployees,
        total: enrichedEmployees.length,
        page,
        totalPages: Math.ceil(enrichedEmployees.length / limit)
      }), 500);
    });
  }
  
  try {
    const response = await apiClient.get('/admin/employees', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get employee by ID (admin)
 * @param {string} id - Employee ID
 * @returns {Promise} - Promise containing employee with assignments
 */
export const getEmployeeById = async (id) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const employee = mockUsers.find(user => user.role === 'employee' && user.id == id);
      
      if (employee) {
        // Enrich with customer assignments
        const assignedCustomers = mockCustomers
          .filter(customer => customer.assignedEmployees.includes(employee.id))
          .map(customer => ({
            id: customer.id,
            name: customer.name,
            projects: customer.projects
          }));
        
        setTimeout(() => resolve({
          ...employee,
          assignedCustomers
        }), 500);
      } else {
        setTimeout(() => reject(new Error('Employee not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.get(`/admin/employees/${id}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update employee (admin)
 * @param {string} id - Employee ID
 * @param {object} employeeData - Updated employee data
 * @returns {Promise} - Promise containing updated employee
 */
export const updateEmployee = async (id, employeeData) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const index = mockUsers.findIndex(user => user.role === 'employee' && user.id == id);
      
      if (index !== -1) {
        mockUsers[index] = { ...mockUsers[index], ...employeeData };
        
        // Get customer assignments
        const assignedCustomers = mockCustomers
          .filter(customer => customer.assignedEmployees.includes(mockUsers[index].id))
          .map(customer => ({
            id: customer.id,
            name: customer.name
          }));
        
        setTimeout(() => resolve({
          ...mockUsers[index],
          assignedCustomers
        }), 500);
      } else {
        setTimeout(() => reject(new Error('Employee not found')), 500);
      }
    });
  }
  
  try {
    const response = await apiClient.put(`/admin/employees/${id}`, employeeData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Assign employee to customers (admin)
 * @param {string} id - Employee ID
 * @param {array} customerIds - Array of customer IDs
 * @returns {Promise} - Promise containing updated employee
 */
export const assignEmployeeToCustomers = async (id, customerIds) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const employee = mockUsers.find(user => user.role === 'employee' && user.id == id);
      
      if (!employee) {
        return setTimeout(() => reject(new Error('Employee not found')), 500);
      }
      
      // Update customer assignments
      mockCustomers.forEach(customer => {
        // If customer should be assigned
        if (customerIds.includes(customer.id)) {
          // And employee isn't already assigned
          if (!customer.assignedEmployees.includes(employee.id)) {
            customer.assignedEmployees.push(employee.id);
          }
        } else {
          // If customer shouldn't be assigned but employee is currently assigned
          const index = customer.assignedEmployees.indexOf(employee.id);
          if (index !== -1) {
            customer.assignedEmployees.splice(index, 1);
          }
        }
      });
      
      // Get updated customer assignments
      const assignedCustomers = mockCustomers
        .filter(customer => customer.assignedEmployees.includes(employee.id))
        .map(customer => ({
          id: customer.id,
          name: customer.name
        }));
      
      setTimeout(() => resolve({
        ...employee,
        assignedCustomers
      }), 500);
    });
  }
  
  try {
    const response = await apiClient.post(`/admin/employees/${id}/customers`, { customerIds });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Create employee (admin)
 * @param {object} employeeData - Employee data
 *   (firstName, lastName, email, department, position, cpr, employeeCode, startDate)
 * @returns {Promise} - Promise containing the created employee (may include temporaryPassword)
 */
export const createEmployee = async (employeeData) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      const newUser = {
        id: mockUsers.length + 1,
        ...employeeData,
        role: 'employee',
        isActive: true,
        createdAt: new Date().toISOString(),
        lastLogin: null,
        assignedCustomers: []
      };
      mockUsers.push(newUser);
      setTimeout(() => resolve({ ...newUser, temporaryPassword: 'Temp123!' }), 500);
    });
  }

  try {
    const response = await apiClient.post('/admin/employees', employeeData);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Remove a single employee from a customer (admin, additive assignment model)
 * @param {string|number} customerId - Customer ID (customers.id)
 * @param {string|number} employeeUserId - Employee user ID (users.id)
 * @returns {Promise} - Promise containing a success message
 */
export const removeEmployeeFromCustomer = async (customerId, employeeUserId) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const customer = mockCustomers.find(c => c.id == customerId);
      if (!customer) {
        return setTimeout(() => reject(new Error('Customer not found')), 300);
      }
      customer.assignedEmployees = customer.assignedEmployees.filter(id => id != employeeUserId);
      setTimeout(() => resolve({ message: 'Employee removed from customer successfully' }), 300);
    });
  }

  try {
    const response = await apiClient.delete(
      `/admin/customers/${customerId}/employees/${employeeUserId}`
    );
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Add a single employee to a customer (admin, additive — does not replace others)
 * @param {string|number} customerId - Customer ID (customers.id)
 * @param {string|number} employeeUserId - Employee user ID (users.id)
 * @returns {Promise} - Promise containing a success message
 */
export const addEmployeeToCustomer = async (customerId, employeeUserId) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const customer = mockCustomers.find(c => c.id == customerId);
      if (!customer) {
        return setTimeout(() => reject(new Error('Customer not found')), 300);
      }
      if (!customer.assignedEmployees.some(id => id == employeeUserId)) {
        customer.assignedEmployees.push(Number(employeeUserId));
      }
      setTimeout(() => resolve({ message: 'Employee added to customer successfully' }), 300);
    });
  }

  try {
    const response = await apiClient.post(
      `/admin/customers/${customerId}/employees/${employeeUserId}`
    );
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Move an employee from one customer to another (admin)
 * @param {string|number} employeeUserId - Employee user ID (users.id)
 * @param {object} params - { fromCustomerId, toCustomerId }
 * @returns {Promise} - Promise containing a success message
 */
export const moveEmployee = async (employeeUserId, { fromCustomerId, toCustomerId } = {}) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve, reject) => {
      const from = mockCustomers.find(c => c.id == fromCustomerId);
      const to = mockCustomers.find(c => c.id == toCustomerId);
      if (!from || !to) {
        return setTimeout(() => reject(new Error('Customer not found')), 300);
      }
      from.assignedEmployees = from.assignedEmployees.filter(id => id != employeeUserId);
      if (!to.assignedEmployees.some(id => id == employeeUserId)) {
        to.assignedEmployees.push(Number(employeeUserId));
      }
      setTimeout(() => resolve({ message: 'Employee moved successfully' }), 300);
    });
  }

  try {
    const response = await apiClient.post(`/admin/employees/${employeeUserId}/move`, {
      fromCustomerId,
      toCustomerId
    });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Import the Danløn medarbejderoversigt (xlsx). Matches Tempus employees by
 * Danløn Medarb.nr or CPR and syncs the master data from Danløn.
 * @param {File} file - the .xlsx export from Danløn
 * @returns {Promise<object>} the import report
 */
export const importDanlonEmployees = async (file) => {
  try {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post('/admin/danlon/import-employees', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Send (or resend) invitations to a set of users. Rotates each user's password
 * (hidden fallback) and 365-day login link, activates the account, and e-mails
 * the localized welcome mail with the link (or returns the login link when it
 * can't be e-mailed so the admin can relay it).
 * @param {Array<number>} userIds - users.id values
 */
export const sendInvitations = async (userIds) => {
  try {
    const response = await apiClient.post('/admin/invitations', { userIds }, { timeout: 120000 });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Extend an employee/customer user's login link by 365 days from now WITHOUT
 * changing the link itself (the mail in their inbox keeps working).
 * @param {number} id - users.id
 * @returns {Promise<{message:string, loginLinkExpiresAt:string}>}
 */
export const renewLoginLink = async (id) => {
  try {
    const response = await apiClient.post(`/admin/users/${id}/renew-token`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Token status overview: every user with the expiry of the credential keeping
 * them signed in (login link for employee/customer, estimated session for
 * admin/support) and tokenState none|ok|expiring|expired.
 * @param {object} params - { role?, isActive? }
 */
export const getTokenStatus = async (params = {}) => {
  try {
    const response = await apiClient.get('/admin/users/token-status', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Official CVR register lookup — company name/address for a Danish CVR number.
 * @param {string} cvr - 8-digit CVR
 * @returns {Promise<{company:{cvr,name,address,active}}>}
 */
export const lookupCvr = async (cvr) => {
  try {
    const response = await apiClient.get(`/admin/cvr/${encodeURIComponent(cvr)}`, { timeout: 30000 });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

const adminService = {
  getEmployment: async (userId) => {
    try {
      const response = await apiClient.get(`/admin/employees/${userId}/employment`);
      return response.data.data;
    } catch (error) { throw handleError(error); }
  },
  overrideEmployment: async (userId, correction) => {
    try {
      const response = await apiClient.post(`/admin/employees/${userId}/employment-overrides`, correction);
      return response.data.data;
    } catch (error) { throw handleError(error); }
  },
  retryEmployment: async (userId, overrideId) => {
    try {
      const response = await apiClient.post(`/admin/employees/${userId}/employment-overrides/${encodeURIComponent(overrideId)}/retry`);
      return response.data.data;
    } catch (error) { throw handleError(error); }
  },
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  activateUser,
  deactivateUser,
  resetUserPassword,
  getMissingWeekHours,
  getMissingFinishedReport,
  remindEmployeeFinished,
  getDashboardStatistics,

  getCustomers,
  getInvoicingCustomerStatus,
  getCustomerById,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  activateCustomer,
  deactivateCustomer,
  assignEmployeesToCustomer,
  getIndustryOptions,
  
  getEmployees,
  getEmployeeById,
  createEmployee,
  updateEmployee,
  assignEmployeeToCustomers,
  removeEmployeeFromCustomer,
  addEmployeeToCustomer,
  moveEmployee,
  importDanlonEmployees,
  sendInvitations,
  renewLoginLink,
  getTokenStatus,
  lookupCvr
};

export default adminService;
